import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { MDBTable, MDBTableHead, MDBTableBody } from 'mdb-react-ui-kit'
import { Row, Col } from 'reactstrap'

const MDBDataTable = ({
    data,
    entries = 10,
    entriesOptions = [10, 25, 50, 100],
    displayEntries = true,
    searching = true,
    paging = true,
    infoLabel = ["Showing", "to", "of", "entries"],
    onSearch,
    sortable, // Extract sortable prop but don't pass it to DOM
    theadColor, // Extract theadColor prop but don't pass it to DOM
    tbodyColor, // Extract tbodyColor prop but don't pass it to DOM
    hover, // Extract hover prop but don't pass it to DOM
    ...tableProps
}) => {
    // All hooks must be called before any conditional returns
    const [currentPage, setCurrentPage] = useState(1)
    const [entriesPerPage, setEntriesPerPage] = useState(entries)
    const [searchQuery, setSearchQuery] = useState('')
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState('asc')

    // Safe defaults for data
    const safeData = data || { rows: [], columns: [] }
    const rows = safeData.rows || []
    const columns = safeData.columns || []

    // Filter and sort data
    const filteredRows = useMemo(() => {
        let result = [...rows]

        // Apply search filter
        if (searching && searchQuery) {
            const query = searchQuery.toLowerCase()
            const searchFields = onSearch || columns.map(col => col.field)

            result = result.filter(row => {
                return searchFields.some(field => {
                    const cellValue = row[field]
                    if (cellValue === null || cellValue === undefined) return false

                    // Handle React elements by checking their text content
                    if (React.isValidElement(cellValue)) {
                        return cellValue.props?.children?.toString().toLowerCase().includes(query)
                    }

                    return cellValue.toString().toLowerCase().includes(query)
                })
            })
        }

        // Apply sorting
        if (sortable && sortColumn) {
            const column = columns.find(col => col.field === sortColumn)
            if (column && column.sort !== 'disabled') {
                result.sort((a, b) => {
                    let aValue, bValue

                    // Use special sort field if available (e.g., for dates)
                    if (a[`__${sortColumn}_sort`] !== undefined && b[`__${sortColumn}_sort`] !== undefined) {
                        aValue = a[`__${sortColumn}_sort`]
                        bValue = b[`__${sortColumn}_sort`]
                    } else {
                        aValue = a[sortColumn]
                        bValue = b[sortColumn]

                        // Handle React elements
                        if (React.isValidElement(aValue)) {
                            aValue = aValue.props?.children?.toString() || ''
                        }
                        if (React.isValidElement(bValue)) {
                            bValue = bValue.props?.children?.toString() || ''
                        }

                        // Convert to comparable values
                        if (typeof aValue === 'string') aValue = aValue.toLowerCase()
                        if (typeof bValue === 'string') bValue = bValue.toLowerCase()
                    }

                    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
                    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
                    return 0
                })
            }
        }

        return result
    }, [rows, searchQuery, searching, onSearch, columns, sortable, sortColumn, sortDirection])

    // Calculate pagination
    const totalPages = Math.ceil(filteredRows.length / entriesPerPage)
    const startIndex = (currentPage - 1) * entriesPerPage
    const endIndex = startIndex + entriesPerPage
    const paginatedRows = filteredRows.slice(startIndex, endIndex)

    // Reset to page 1 when entries per page changes, search changes, or sort changes
    useEffect(() => {
        setCurrentPage(1)
    }, [entriesPerPage, searchQuery, sortColumn, sortDirection])

    // Handle column header click for sorting
    const handleSort = (columnField) => {
        const column = columns.find(col => col.field === columnField)
        if (!sortable || !column || column.sort === 'disabled') return

        if (sortColumn === columnField) {
            // Toggle sort direction
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            // Set new sort column
            setSortColumn(columnField)
            setSortDirection(column.sort || 'asc')
        }
    }

    // Handle the no data case after all hooks are called
    if (!data || !rows.length || !columns.length) {
        return <div>No data available</div>
    }

    // Build className for MDBTable, merging hover with existing className
    const tableClassName = hover
        ? (tableProps.className ? `${tableProps.className} table-hover` : 'table-hover')
        : tableProps.className

    // Create sanitized tableProps without className (we'll pass it separately)
    // eslint-disable-next-line no-unused-vars
    const { className, ...sanitizedTableProps } = tableProps

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page)
        }
    }

    const handleEntriesChange = (e) => {
        setEntriesPerPage(parseInt(e.target.value))
    }

    const renderPagination = () => {
        if (!paging || totalPages <= 1) return null

        const pages = []
        const maxPagesToShow = 5

        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2))
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1)
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i)
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="dataTables_info">
                    {infoLabel[0]} {startIndex + 1} {infoLabel[1]} {Math.min(endIndex, filteredRows.length)} {infoLabel[2]} {filteredRows.length} {infoLabel[3] || 'entries'}
                </div>
                <nav>
                    <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                            >
                                First
                            </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                        </li>
                        {pages.map(page => (
                            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                            >
                                Last
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        )
    }

    return (
        <div className="dataTables_wrapper">
            <Row className="mb-3">
                {displayEntries && (
                    <Col md={6} className="d-flex align-items-center">
                        <label className="me-2 mb-0">Show</label>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto', display: 'inline-block' }}
                            value={entriesPerPage}
                            onChange={handleEntriesChange}
                        >
                            {entriesOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                        <label className="ms-2 mb-0">entries</label>
                    </Col>
                )}
                {searching && (
                    <Col md={displayEntries ? 6 : 12} className={displayEntries ? 'text-end' : ''}>
                        <div className="d-flex justify-content-end">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ maxWidth: '300px' }}
                            />
                        </div>
                    </Col>
                )}
            </Row>

            <div className="table-responsive">
                <MDBTable {...sanitizedTableProps} className={tableClassName}>
                    <MDBTableHead color={theadColor}>
                        <tr>
                            {columns.map((column, index) => {
                                const isSortable = sortable && column.sort !== 'disabled'
                                const isSorted = sortColumn === column.field
                                const sortClass = isSortable
                                    ? isSorted
                                        ? sortDirection === 'asc'
                                            ? 'sorting_asc'
                                            : 'sorting_desc'
                                        : 'sorting'
                                    : ''

                                // Render sorting icons
                                const renderSortIcon = () => {
                                    if (!isSortable) return null

                                    if (isSorted) {
                                        return sortDirection === 'asc' ? (
                                            <i className="fas fa-arrow-up ms-2" style={{ fontSize: '14px', opacity: 1, color: '#007bff' }}></i>
                                        ) : (
                                            <i className="fas fa-arrow-down ms-2" style={{ fontSize: '14px', opacity: 1, color: '#007bff' }}></i>
                                        )
                                    } else {
                                        return (
                                            <span className="ms-2" style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: '0.5', fontSize: '10px' }}>
                                                <i className="fas fa-arrow-up" style={{ fontSize: '10px', opacity: 0.4, marginBottom: '1px' }}></i>
                                                <i className="fas fa-arrow-down" style={{ fontSize: '10px', opacity: 0.4 }}></i>
                                            </span>
                                        )
                                    }
                                }

                                return (
                                    <th
                                        key={index}
                                        className={sortClass || undefined}
                                        onClick={() => isSortable && handleSort(column.field)}
                                        style={{ cursor: isSortable ? 'pointer' : 'default' }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            {column.label}
                                            {renderSortIcon()}
                                        </span>
                                    </th>
                                )
                            })}
                        </tr>
                    </MDBTableHead>
                    <MDBTableBody color={tbodyColor}>
                        {paginatedRows.length > 0 ? (
                            paginatedRows.map((row, rowIndex) => (
                                <tr key={rowIndex} className={row.__rowClassName || ''}>
                                    {columns.map((column, colIndex) => {
                                        const cellValue = column.rowNumber
                                            ? startIndex + rowIndex + 1
                                            : row[column.field]

                                        return (
                                            <td key={colIndex}>{cellValue}</td>
                                        )
                                    })}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </MDBTableBody>
                </MDBTable>
            </div>

            {renderPagination()}
        </div>
    )
}

export default MDBDataTable

MDBDataTable.propTypes = {
    data: PropTypes.shape({
        columns: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.node,
                field: PropTypes.string.isRequired,
                sort: PropTypes.string,
                rowNumber: PropTypes.bool
            })
        ),
        rows: PropTypes.arrayOf(PropTypes.object)
    }),
    entries: PropTypes.number,
    entriesOptions: PropTypes.arrayOf(PropTypes.number),
    displayEntries: PropTypes.bool,
    searching: PropTypes.bool,
    paging: PropTypes.bool,
    infoLabel: PropTypes.arrayOf(PropTypes.string),
    onSearch: PropTypes.arrayOf(PropTypes.string),
    sortable: PropTypes.bool,
    theadColor: PropTypes.string,
    tbodyColor: PropTypes.string,
    hover: PropTypes.bool
}
